import React from 'react';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '@/i18n/I18nContext';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import IconButton from '@mui/material/IconButton';
import CodeIcon from '@mui/icons-material/Code';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import Divider from '@mui/material/Divider';

const BrewfatherSetupPage: React.FC = () => {
	const navigate = useNavigate();
	const { t } = useI18n();

	return (
		<Container maxWidth="md" sx={{ py: 4 }}>
			<Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
				<IconButton onClick={() => navigate('/dashboard')}>
					<ArrowBackIcon />
				</IconButton>
				<Typography variant="h4" fontWeight={800}>
					{t.brewfatherSetup.title}
				</Typography>
			</Box>

			<Paper sx={{ p: 4, mb: 3 }}>
				<Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
					{t.brewfatherSetup.introduction}
				</Typography>
				<Typography variant="body1" color="text.secondary" paragraph>
					{t.brewfatherSetup.introductionText}
				</Typography>
			</Paper>

			<Paper sx={{ p: 4, mb: 3 }}>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
					<AccountCircleIcon color="primary" />
					<Typography variant="h6" fontWeight={700}>
						{t.brewfatherSetup.step1Title}
					</Typography>
				</Box>
				<Typography variant="body1" color="text.secondary" paragraph>
					{t.brewfatherSetup.step1Description}
				</Typography>
				<Box sx={{ mt: 2, mb: 2 }}>
					<Link href="https://web.brewfather.app/settings/api" target="_blank" rel="noopener noreferrer">
						<Button variant="contained" startIcon={<AccountCircleIcon />}>
							{t.brewfatherSetup.openBrewfatherSettings}
						</Button>
					</Link>
				</Box>
			</Paper>

			<Paper sx={{ p: 4, mb: 3 }}>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
					<VpnKeyIcon color="primary" />
					<Typography variant="h6" fontWeight={700}>
						{t.brewfatherSetup.step2Title}
					</Typography>
				</Box>
				<Typography variant="body1" color="text.secondary" paragraph>
					{t.brewfatherSetup.step2Description}
				</Typography>
				<Box sx={{ bgcolor: 'background.default', p: 2, borderRadius: 1, mt: 2 }}>
					<Typography variant="body2" component="pre" sx={{ fontFamily: 'monospace', mb: 1 }}>
						{t.brewfatherSetup.apiKeyLocation}
					</Typography>
				</Box>
			</Paper>

			<Paper sx={{ p: 4, mb: 3 }}>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
					<CodeIcon color="primary" />
					<Typography variant="h6" fontWeight={700}>
						{t.brewfatherSetup.step3Title}
					</Typography>
				</Box>
				<Typography variant="body1" color="text.secondary" paragraph>
					{t.brewfatherSetup.step3Description}
				</Typography>
				<Box sx={{ bgcolor: 'background.default', p: 2, borderRadius: 1, mt: 2 }}>
					<Typography variant="body2" component="pre" sx={{ fontFamily: 'monospace', mb: 1 }}>
						{t.brewfatherSetup.userIdLocation}
					</Typography>
				</Box>
			</Paper>

			<Paper sx={{ p: 4, mb: 3 }}>
				<Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
					{t.brewfatherSetup.step4Title}
				</Typography>
				<Typography variant="body1" color="text.secondary" paragraph>
					{t.brewfatherSetup.step4Description}
				</Typography>
				<List>
					<ListItem>
						<ListItemIcon>
							<CheckCircleIcon color="success" />
						</ListItemIcon>
						<ListItemText primary={t.brewfatherSetup.checklist1} />
					</ListItem>
					<ListItem>
						<ListItemIcon>
							<CheckCircleIcon color="success" />
						</ListItemIcon>
						<ListItemText primary={t.brewfatherSetup.checklist2} />
					</ListItem>
					<ListItem>
						<ListItemIcon>
							<CheckCircleIcon color="success" />
						</ListItemIcon>
						<ListItemText primary={t.brewfatherSetup.checklist3} />
					</ListItem>
				</List>
			</Paper>

			<Paper sx={{ p: 4, mb: 3 }}>
				<Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
					{t.brewfatherSetup.importantNotes}
				</Typography>
				<Stack spacing={2}>
					<Box>
						<Typography variant="subtitle2" fontWeight={700} color="warning.main">
							{t.brewfatherSetup.note1Title}
						</Typography>
						<Typography variant="body2" color="text.secondary">
							{t.brewfatherSetup.note1Text}
						</Typography>
					</Box>
					<Box>
						<Typography variant="subtitle2" fontWeight={700} color="warning.main">
							{t.brewfatherSetup.note2Title}
						</Typography>
						<Typography variant="body2" color="text.secondary">
							{t.brewfatherSetup.note2Text}
						</Typography>
					</Box>
					<Box>
						<Typography variant="subtitle2" fontWeight={700} color="info.main">
							{t.brewfatherSetup.note3Title}
						</Typography>
						<Typography variant="body2" color="text.secondary">
							{t.brewfatherSetup.note3Text}
						</Typography>
					</Box>
				</Stack>
			</Paper>

			<Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
				<Button variant="outlined" onClick={() => navigate('/dashboard')}>
					{t.common.cancel}
				</Button>
				<Button variant="contained" onClick={() => navigate('/dashboard')}>
					{t.brewfatherSetup.goToDashboard}
				</Button>
			</Box>
		</Container>
	);
};

export default BrewfatherSetupPage;
